import { clsx } from "clsx";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDeepCompareEffect } from "react-use";
import { useDropzone } from "react-dropzone";
import { ImageUp, Loader2 } from "lucide-react";

import {
  ACCEPTED_IMAGE_TYPES,
  ImageProcessingError,
  fileToAvatarDataUrl,
} from "@/ai-personas/imageUpload";
import { initialsForUser } from "@/components/users/Avatar";
import { avatarColorFor } from "@/components/users/avatarColor";
import { User } from "@/components/users/types";
import {
  UpdateUserProfilePayload,
  updateUserProfile,
} from "@/account/api/user";
import { inferredNameFor } from "@/account/components/EditProfileForm/nameFromEmail";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { Input } from "@/design-system/components/Input/Input";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { toast } from "@/design-system/components/Toast/Toast";
import useUsers from "@/hooks/useUsers";
import { unpackRequestErrorMessage } from "@/utils/request";

// The floating-label Input only wires aria-describedby for its stacked
// variant, so the hint is associated with the field explicitly here.
const TITLE_HINT_ID = "title-personalization-hint";

const ACCEPTED_AVATAR_TYPES = Object.fromEntries(
  ACCEPTED_IMAGE_TYPES.map((t) => [t, []]),
);

function initialValues(currentUser?: User): UpdateUserProfilePayload {
  const inferred = inferredNameFor(currentUser);
  return {
    first_name: currentUser?.first_name || inferred.first_name,
    last_name: currentUser?.last_name || inferred.last_name,
    title: currentUser?.title ?? "",
  };
}

export interface EditProfileFormProps {
  onSaved?: () => void;
  onCancel?: () => void;
}

export function EditProfileForm({ onSaved, onCancel }: EditProfileFormProps) {
  const { currentUser } = useUsers();
  const [avatarOverride, setAvatarOverride] = useState<string | undefined>();
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarProcessing, setAvatarProcessing] = useState(false);
  const [avatarImageFailed, setAvatarImageFailed] = useState(false);

  const { register, handleSubmit, formState, setError, reset } =
    useForm<UpdateUserProfilePayload>({
      defaultValues: initialValues(currentUser),
    });

  useDeepCompareEffect(() => {
    reset(initialValues(currentUser));
    setAvatarImageFailed(false);
  }, [reset, currentUser]);

  const onDrop = useCallback(async (accepted: File[]) => {
    const file = accepted[0];
    if (!file) {
      return;
    }
    setAvatarError(null);
    setAvatarProcessing(true);
    try {
      setAvatarOverride(await fileToAvatarDataUrl(file));
      setAvatarImageFailed(false);
    } catch (err) {
      setAvatarError(
        err instanceof ImageProcessingError
          ? err.message
          : "Couldn't process that image.",
      );
    } finally {
      setAvatarProcessing(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_AVATAR_TYPES,
    multiple: false,
    disabled: avatarProcessing,
  });

  const onSubmit = useCallback(
    async (values: UpdateUserProfilePayload) => {
      try {
        await updateUserProfile({
          ...values,
          ...(avatarOverride !== undefined ? { avatar: avatarOverride } : {}),
        });
        toast.success("Profile saved");
        onSaved?.();
      } catch (error) {
        setError("root", { message: unpackRequestErrorMessage(error) });
      }
    },
    [avatarOverride, onSaved, setError],
  );

  const guessedFromEmail = useMemo(() => {
    const inferred = inferredNameFor(currentUser);
    return !!(inferred.first_name || inferred.last_name);
  }, [currentUser]);

  const previewAvatar = avatarOverride ?? currentUser?.avatar;
  const showAvatarImage = !!previewAvatar && !avatarImageFailed;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="border-borderSubtle flex items-center gap-4 border-b pb-4">
        <div
          {...getRootProps()}
          className={clsx(
            "group relative size-14 shrink-0 cursor-pointer overflow-hidden rounded-full transition",
            isDragActive && "ring-interactive-selected ring-2 ring-offset-2",
          )}
        >
          <input {...getInputProps()} aria-label="Profile image" />
          {showAvatarImage ? (
            <img
              src={previewAvatar}
              alt=""
              aria-hidden
              className="size-14 object-cover"
              onError={() => setAvatarImageFailed(true)}
            />
          ) : (
            <span
              className={clsx(
                "flex size-14 items-center justify-center text-white uppercase",
                currentUser
                  ? avatarColorFor(currentUser.email ?? "")
                  : "bg-surface-hover",
              )}
            >
              {currentUser ? initialsForUser(currentUser) : ""}
            </span>
          )}
          <div
            className={clsx(
              "absolute inset-0 flex items-center justify-center bg-black/50 text-white transition",
              avatarProcessing || isDragActive
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100",
              // The circle is the only drop target now, so it has to stay
              // discoverable even when there is no image behind the overlay.
              !showAvatarImage && "opacity-100",
            )}
          >
            {avatarProcessing ? (
              <Loader2 className="size-5 animate-spin" aria-hidden />
            ) : (
              <ImageUp className="size-5" aria-hidden />
            )}
          </div>
        </div>
        <div className="min-w-0">
          <span className="text-textSubtle text-body-sm block truncate">
            {currentUser?.email}
          </span>
          {/* Outside the dropzone root: inside it, the click would bubble to
              the root and open the file dialog twice. */}
          <Button type="button" variant="link" onClick={open}>
            Change profile image
          </Button>
        </div>
      </div>
      {avatarError && <p className="text-textError text-sm">{avatarError}</p>}

      {guessedFromEmail && (
        <Banner kind="note" type="info" message="Check your details">
          We filled in some fields for you. Make sure they are correct, then
          save.
        </Banner>
      )}

      <Input
        label="First name"
        {...register("first_name")}
        error={formState.errors.first_name}
      />
      <Input
        label="Last name"
        {...register("last_name")}
        error={formState.errors.last_name}
      />
      <Input
        label="Title"
        {...register("title")}
        error={formState.errors.title}
        aria-describedby={TITLE_HINT_ID}
        description={
          <span id={TITLE_HINT_ID}>
            Sharing your title helps us tailor Freshpaint to the work you do.
          </span>
        }
      />

      <div className="flex justify-end gap-4 pt-4">
        {formState.errors.root && (
          <p className="text-textError text-sm">
            {formState.errors.root.message}
          </p>
        )}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={formState.isSubmitting}
          leadingIcon={
            formState.isSubmitting ? (
              <LoadingSpinner className="size-4" />
            ) : undefined
          }
        >
          Save
        </Button>
      </div>
    </form>
  );
}
